import { motion } from "framer-motion";

const CENTER = { x: 50, y: 50 };
const RADIUS = 42;

const disciplines = [
  { label: "Built + Environment", color: "#C9A24B" },
  { label: "Product", color: "#C9A24B" },
  { label: "More to learn", color: "#1A1A1A", ghost: true },
  { label: "Media + Tech", color: "#6B8F71" },
  { label: "Art", color: "#C9A24B" },
  { label: "Research", color: "#6B8F71" },
];

const nodes = disciplines.map((d, i) => {
  const angle = ((-90 + i * 60) * Math.PI) / 180;
  return {
    ...d,
    x: CENTER.x + RADIUS * Math.cos(angle),
    y: CENTER.y + RADIUS * Math.sin(angle),
  };
});

const EASE = [0.22, 1, 0.36, 1];
const VIEWPORT = { once: true };
const RING_INITIAL = { opacity: 0 };
const RING_IN_VIEW = { opacity: 0.25 };
const RING_TRANSITION = { duration: 1.4, ease: "easeOut" };
const LINE_INITIAL = { pathLength: 0, opacity: 0 };
const CENTER_INITIAL = { opacity: 0, scale: 0.7 };
const CENTER_IN_VIEW = { opacity: 1, scale: 1 };
const CENTER_TRANSITION = { duration: 0.8, ease: EASE };

export const OverlapDiagram = () => {
  return (
    <div
      data-testid="overlap-diagram"
      className="relative w-full max-w-[480px] mx-auto"
      style={{ aspectRatio: "1 / 1" }}
    >
      {/* connector lines + orbit ring */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" fill="none" preserveAspectRatio="xMidYMid meet">
        <motion.circle
          cx={CENTER.x}
          cy={CENTER.y}
          r={RADIUS}
          stroke="#1A1A1A"
          strokeWidth="0.25"
          strokeDasharray="0.6 2.4"
          initial={RING_INITIAL}
          whileInView={RING_IN_VIEW}
          viewport={VIEWPORT}
          transition={RING_TRANSITION}
        />
        {nodes.map((n, i) => (
          <motion.line
            key={n.label}
            x1={CENTER.x}
            y1={CENTER.y}
            x2={n.x}
            y2={n.y}
            stroke={n.color}
            strokeWidth="0.4"
            strokeDasharray="1.4 2"
            strokeLinecap="round"
            initial={LINE_INITIAL}
            whileInView={{ pathLength: 1, opacity: n.ghost ? 0.35 : 0.7 }}
            viewport={VIEWPORT}
            transition={{ duration: 1.1, delay: 0.4 + i * 0.12, ease: "easeInOut" }}
          />
        ))}
      </svg>

      {/* center node — positioning wrapper (no animation transform) */}
      <div className="absolute left-1/2 top-1/2" style={{ transform: "translate(-50%, -50%)" }}>
        <motion.div
          className="w-36 h-36 sm:w-48 sm:h-48 rounded-full bg-[#1A1A1A] text-[#FFFFFF] flex flex-col items-center justify-center text-center px-5 shadow-[0_18px_40px_-22px_rgba(26,26,26,0.6)]"
          initial={CENTER_INITIAL}
          whileInView={CENTER_IN_VIEW}
          viewport={VIEWPORT}
          transition={CENTER_TRANSITION}
        >
          <span className="font-mono text-[8px] sm:text-[10px] tracking-[0.28em] uppercase text-[#C9A24B]">
            one instinct
          </span>
          <span className="font-serif text-base sm:text-xl leading-[1.1] mt-1.5">
            systems &amp;
            <br />
            design thinking
          </span>
        </motion.div>
      </div>

      {/* discipline nodes — positioning wrapper + inner motion */}
      {nodes.map((n, i) => (
        <div
          key={n.label}
          className="absolute"
          style={{ left: `${n.x}%`, top: `${n.y}%`, transform: "translate(-50%, -50%)" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border bg-[#FFFFFF] px-3 py-1.5 sm:px-4 sm:py-2 transition-transform duration-500 hover:-translate-y-1 ${
                n.ghost ? "border-dashed opacity-80" : ""
              }`}
              style={{ borderColor: n.color }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: n.color }} />
              <span
                className={`font-sans text-xs sm:text-sm ${n.ghost ? "italic" : ""}`}
                style={{ color: n.color }}
              >
                {n.label}
              </span>
            </div>
          </motion.div>
        </div>
      ))}
    </div>
  );
};

export default OverlapDiagram;
